import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useAtomValue } from 'jotai'
import { useI18n } from '../../provider/i18n'
import { BasketContext, isConsultationBasketItem } from '../../provider/basket'
import { AuthContext } from '../../provider/auth'
import {
  consultationsSubscriptionAtom,
  profilesSubscriptionAtom,
} from '../../store/subscription.atoms'
import { ConsultationFragment } from '../../generated/urql.anonymous'
import ProfileSelectorModal, {
  ProfileSelectorModalType,
} from '../../components/ProfileSelectorModal'
import { TailSpin } from 'react-loader-spinner'
import { Blue300 } from '../../lib/colors'
import { ConsultationItem } from '../../components/ConsultationItem'

const Consultations = () => {
  const [isInited, setIsInited] = useState(false)
  const basket = useContext(BasketContext)
  const auth = useContext(AuthContext)
  const i18n = useI18n()
  const [{ data: consultationsData, fetching: consultationsFetching }] =
    useAtomValue(consultationsSubscriptionAtom)
  const [{ data: profilesData }] = useAtomValue(profilesSubscriptionAtom)
  const profileSelectorModalRef = useRef<ProfileSelectorModalType>(null)

  const consultations = consultationsData?.consultation || []

  const isFetching = useMemo(
    () => consultationsFetching && !isInited,
    [consultationsFetching, isInited]
  )

  useEffect(() => {
    if (!isFetching) {
      setIsInited(true)
    }
  }, [isFetching])

  const onConsultationAddClick = useCallback(
    (consultation: ConsultationFragment) => {
      const profilesCount = profilesData?.client_profile.length || 0

      if (!auth.isLogged || profilesCount === 0) {
        basket.addItem({ consultation })
      } else if (profilesCount === 1) {
        basket.addItem({
          consultation,
          profile: profilesData?.client_profile[0],
        })
      } else {
        profileSelectorModalRef.current?.show({
          onChange: (profile) => {
            basket.addItem({ consultation, profile })
          },
          filter: (profile) =>
            !basket.items.find(
              (item) =>
                isConsultationBasketItem(item) &&
                item.consultation.id === consultation.id &&
                item.profile?.id === profile.id
            ),
        })
      }
    },
    [auth.isLogged, basket, profilesData?.client_profile]
  )

  if (isFetching) {
    return (
      <TailSpin
        color={Blue300}
        height={32}
        width={32}
        wrapperClass="flex justify-center p-8"
      />
    )
  }

  return (
    <>
      <div className="text-blue-700 my-5 flex items-center gap-1">
        {i18n.t('screens.catalog.index.consultations.header')}
      </div>
      <ul className="md:mx-5 flex flex-col gap-5 mb-10">
        {consultations.map((consultation) => (
          <li key={consultation.id}>
            <ConsultationItem
              consultation={consultation}
              onConsultationAddClick={() =>
                onConsultationAddClick(consultation)
              }
              showAddButton={
                auth.isLogged
                  ? basket.items
                      .filter(isConsultationBasketItem)
                      .filter(
                        ({ consultation: { id } }) => consultation.id === id
                      ).length < (profilesData?.client_profile.length || 0)
                  : !basket.items.find(
                      (item) =>
                        isConsultationBasketItem(item) &&
                        consultation.id === item.consultation.id
                    )
              }
            />
          </li>
        ))}
      </ul>
      <ProfileSelectorModal ref={profileSelectorModalRef} />
    </>
  )
}

export default Consultations
